import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bug, Camera, CheckCircle2, Edit3, ImagePlus, Loader2, Paperclip, Send, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  fileToIssueAttachment,
  IssueSeverity,
  recordClientError,
  reportSystemIssue,
  submitIssueReport,
} from '@/lib/bug-reporting';
import { ScreenshotEditor } from '@/components/support/ScreenshotEditor';

const MAX_FILE_BYTES = 12 * 1024 * 1024;

type ScreenshotEvidence = {
  id: string;
  name: string;
  dataUrl: string;
  caption: string;
};

export function BugReportWidget() {
  const { user, isAuthenticated } = useAuth();
  const { tenant } = useTenant();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<IssueSeverity>('blocking');
  const [files, setFiles] = useState<File[]>([]);
  const [screenshots, setScreenshots] = useState<ScreenshotEvidence[]>([]);
  const [editingScreenshotId, setEditingScreenshotId] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const windowWithContext = window as unknown as { meddSimIssueContext?: Record<string, unknown> };
    windowWithContext.meddSimIssueContext = {
      ...(windowWithContext.meddSimIssueContext || {}),
      tenantId: tenant?.tenant_id || user.tenant_id || null,
      tenantName: tenant?.tenant_name || null,
      tenantSlug: tenant?.tenant_slug || null,
      userId: user.id,
      userEmail: user.email,
      userRoles: user.roles || [],
      companyId: user.company_id || null,
    };
    return () => {
      const current = windowWithContext.meddSimIssueContext || {};
      windowWithContext.meddSimIssueContext = {
        ...current,
        userId: null,
        userEmail: null,
        userRoles: [],
        companyId: null,
      };
    };
  }, [isAuthenticated, tenant?.tenant_id, tenant?.tenant_name, tenant?.tenant_slug, user]);

  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      reportSystemIssue(event.error || event.message, {
        source: 'window.error',
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      reportSystemIssue(event.reason || 'Unhandled promise rejection', {
        source: 'unhandledrejection',
      });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  const oversizedFiles = useMemo(() => files.filter(file => file.size > MAX_FILE_BYTES), [files]);
  const canSubmit = description.trim().length >= 8 && !isSubmitting && oversizedFiles.length === 0;

  if (!isAuthenticated || !user) return null;

  const addScreenshotDataUrl = (dataUrl: string, name?: string) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setScreenshots(current => {
      const nextNumber = current.length + 1;
      return [
        ...current,
        {
          id,
          name: name || `Screenshot ${nextNumber}`,
          dataUrl,
          caption: '',
        },
      ].slice(0, 8);
    });
    setEditingScreenshotId(id);
  };

  const captureScreenWithPicker = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.warning('Screen capture is not available in this browser. Please upload a screenshot instead.');
      return;
    }

    setIsCapturing(true);
    setOpen(false);
    let stream: MediaStream | null = null;
    try {
      await new Promise(resolve => window.setTimeout(resolve, 180));
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      await new Promise(resolve => window.setTimeout(resolve, 180));

      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      const width = settings.width || video.videoWidth || window.innerWidth;
      const height = settings.height || video.videoHeight || window.innerHeight;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not prepare screenshot canvas');
      ctx.drawImage(video, 0, 0, width, height);

      const dataUrl = canvas.toDataURL('image/png', 0.85);
      addScreenshotDataUrl(dataUrl, `Screenshot ${screenshots.length + 1}`);
      toast.success('Screenshot added. You can edit it now or add another screenshot.');
    } catch (error) {
      const name = error instanceof DOMException ? error.name : '';
      if (name !== 'NotAllowedError') {
        recordClientError(error, { source: 'bug-report-screen-picker' });
        toast.warning('Could not take a screenshot. You can upload a screenshot file instead.');
      }
    } finally {
      stream?.getTracks().forEach(item => item.stop());
      setIsCapturing(false);
      setOpen(true);
    }
  };

  const handleScreenshotUploads = async (uploadedFiles: FileList | null) => {
    const imageFiles = Array.from(uploadedFiles || [])
      .filter(file => file.type.startsWith('image/'))
      .slice(0, Math.max(0, 8 - screenshots.length));

    let firstNewId: string | null = null;
    for (const file of imageFiles) {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error || new Error('Failed to read screenshot'));
        reader.readAsDataURL(file);
      });
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      if (!firstNewId) firstNewId = id;
      setScreenshots(current => {
        const nextNumber = current.length + 1;
        return [
          ...current,
          {
            id,
            name: file.name.replace(/\.(png|jpg|jpeg|webp)$/i, '') || `Screenshot ${nextNumber}`,
            dataUrl,
            caption: '',
          },
        ].slice(0, 8);
      });
    }
    if (firstNewId) {
      setEditingScreenshotId(firstNewId);
      toast.success(imageFiles.length > 1 ? 'Screenshots uploaded. Edit each one before sending.' : 'Screenshot uploaded. You can edit it now.');
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const attachments = await Promise.all(files.map(fileToIssueAttachment));
      const title = description.trim().split('\n')[0].slice(0, 140) || 'MEDD SIM issue reported';
      await submitIssueReport({
        source: 'manual',
        severity,
        title,
        description,
        screenshotDataUrl: null,
        screenshots: screenshots.map((screenshot, index) => ({
          name: screenshot.name || `Screenshot ${index + 1}`,
          dataUrl: screenshot.dataUrl,
          caption: screenshot.caption,
        })),
        attachments,
      });
      toast.success('Issue report sent. Thank you, we have the screenshot and details.');
      setOpen(false);
      setDescription('');
      setSeverity('blocking');
      setFiles([]);
      setScreenshots([]);
    } catch (error) {
      recordClientError(error, { source: 'bug-report-submit' });
      toast.error(error instanceof Error ? error.message : 'Failed to send issue report');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-bug-report-ignore="true">
      {/* Compact icon FAB stacked above the AI assistant button so it never
          covers the sidebar user footer. The AI panel (z-50) sits above it
          when open. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Report an issue"
            className="fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full bg-[#1E3A8A] p-0 shadow-lg hover:bg-[#142862]"
          >
            <Bug className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">Report an issue</TooltipContent>
      </Tooltip>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[92vh] max-w-4xl flex-col overflow-hidden p-0" data-bug-report-ignore="true">
          <DialogHeader className="border-b px-6 pb-4 pt-6">
            <DialogTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-[#1E3A8A]" />
              Report an issue
            </DialogTitle>
            <DialogDescription>
              Send annotated screenshots, browser details, and any problematic files to the support team.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-5 overflow-y-auto px-6 py-5">
            <div className="grid gap-2">
              <Label htmlFor="bug-severity">How serious is it?</Label>
              <Select value={severity} onValueChange={(value) => setSeverity(value as IssueSeverity)}>
                <SelectTrigger id="bug-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minor">Minor issue</SelectItem>
                  <SelectItem value="blocking">Blocking my workflow</SelectItem>
                  <SelectItem value="urgent">Urgent / event affected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="bug-description">What happened?</Label>
              <Textarea
                id="bug-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Tell us what you expected, what happened, and which page or step caused it."
                rows={5}
              />
            </div>

            <div className="grid gap-3">
              <div className="flex items-center justify-between gap-3">
                <Label>Screenshots</Label>
                <Button type="button" variant="outline" size="sm" onClick={captureScreenWithPicker} disabled={isCapturing || screenshots.length >= 8}>
                  {isCapturing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                  Take screenshot
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {screenshots.length > 0 ? (
                  screenshots.map((screenshot, index) => (
                    <div key={screenshot.id} className="rounded-lg border bg-muted/30 overflow-hidden">
                      <div className="aspect-video bg-white flex items-center justify-center overflow-hidden">
                        <img src={screenshot.dataUrl} alt={screenshot.name} className="h-full w-full object-contain" />
                      </div>
                      <div className="space-y-2 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium">{screenshot.name}</p>
                          <div className="flex gap-1">
                            <Button type="button" variant="outline" size="sm" onClick={() => setEditingScreenshotId(screenshot.id)}>
                              <Edit3 className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => setScreenshots(current => current.filter(item => item.id !== screenshot.id))}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <Textarea
                          value={screenshot.caption}
                          onChange={(event) => setScreenshots(current => current.map(item => item.id === screenshot.id ? { ...item, caption: event.target.value } : item))}
                          placeholder={`Caption for screenshot ${index + 1}`}
                          rows={2}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="md:col-span-2 rounded-lg border bg-muted/30 min-h-[140px] flex items-center justify-center text-sm text-muted-foreground gap-2">
                    {isCapturing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                    {isCapturing ? 'Opening screen picker...' : 'No screenshots added yet'}
                  </div>
                )}
              </div>
              <div className="rounded-lg border bg-slate-50 p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium">Add screenshots</p>
                    <p className="text-xs text-muted-foreground">
                      Use Take screenshot to choose a screen/window, or upload screenshots you already captured.
                      {screenshots.length > 0 ? ` You can add ${8 - screenshots.length} more.` : ''}
                    </p>
                  </div>
                  <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground">
                    <ImagePlus className="w-4 h-4" />
                    Upload screenshots
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      multiple
                      className="sr-only"
                      onChange={(event) => {
                        handleScreenshotUploads(event.target.files);
                        event.currentTarget.value = '';
                      }}
                      disabled={screenshots.length >= 8}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="bug-files">Attach files</Label>
              <input
                id="bug-files"
                type="file"
                multiple
                accept="image/*,.pdf,.docx,.pptx,.txt"
                onChange={(event) => setFiles(Array.from(event.target.files || []).slice(0, 5))}
                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-[#EFF6FF] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-[#1E3A8A] hover:file:bg-[#DBEAFE]"
              />
              <div className="flex flex-wrap gap-2">
                {files.map(file => (
                  <Badge key={`${file.name}-${file.size}`} variant={file.size > MAX_FILE_BYTES ? 'destructive' : 'outline'} className="gap-1">
                    <Paperclip className="w-3 h-3" />
                    {file.name} ({(file.size / 1024 / 1024).toFixed(1)}MB)
                    <button
                      type="button"
                      onClick={() => setFiles(current => current.filter(item => item !== file))}
                      className="ml-1"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              {oversizedFiles.length > 0 && (
                <p className="text-sm text-destructive flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Each issue attachment must be 12MB or smaller.
                </p>
              )}
            </div>

            <div className="rounded-lg bg-[#EFF6FF] border border-[#C7CEE7] p-3 text-sm text-[#1E3A8A] flex gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              Reports are emailed to the development team and saved in the MEDD SIM issue queue.
            </div>
          </div>

          <div className="flex flex-shrink-0 justify-end gap-3 border-t bg-background px-6 py-4 shadow-[0_-8px_20px_rgba(15,23,42,0.06)]">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={submit} disabled={!canSubmit} className="bg-[#1E3A8A] hover:bg-[#142862]">
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ScreenshotEditor
        open={Boolean(editingScreenshotId)}
        imageDataUrl={screenshots.find(item => item.id === editingScreenshotId)?.dataUrl || null}
        onCancel={() => setEditingScreenshotId(null)}
        onSave={(dataUrl) => {
          setScreenshots(current => current.map(item => item.id === editingScreenshotId ? { ...item, dataUrl } : item));
          setEditingScreenshotId(null);
        }}
      />
    </div>
  );
}
